import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";

// Generic GraphQL ops (category based)
const SAVE_DRAFT_STATE = gql`
  mutation SaveDraftState($category: String!, $payload: JSON!) {
    saveDraftState(input: { category: $category, payload: $payload }) {
      category
      updatedAt
    }
  }
`;

const CLEAR_DRAFT_STATE = gql`
  mutation ClearDraftState($category: String!) {
    clearDraftState(category: $category)
  }
`;

const GET_DRAFT_STATE = gql`
  query DraftState($category: String!) {
    draftState(category: $category) {
      payload
      updatedAt
    }
  }
`;

interface HookFlags {
  hasRestoredState: boolean;
  isRestoring: boolean;
  isSaving: boolean;
  clearDraft: () => Promise<void>;
}

/**
 * Generic persistence hook that mirrors the behaviour of the older
 * `useCheckoutPersistence` but works for any draft *category*.
 *
 * @param category           unique key such as "CHECKOUT" or "ONBOARDING"
 * @param snapshotFn         returns the current draft payload (any serialisable JSON)
 * @param hydrateFn          called once when a server-side draft is restored
 */
export function useDraftPersistence<T = any>(
  category: string,
  snapshotFn: () => T,
  hydrateFn: (payload: T) => void,
): HookFlags {
  const [hasRestoredState, setHasRestoredState] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isInitialLoadRef = useRef(true);

  // GraphQL hooks
  const [saveDraftState] = useMutation(SAVE_DRAFT_STATE);
  const [clearDraftStateMutation] = useMutation(CLEAR_DRAFT_STATE);
  const [loadDraftState] = useLazyQuery(GET_DRAFT_STATE);

  // Check if the current visitor already holds a valid auth token. Drafts are stored server-side
  // **per user**, so there is no point in hitting the backend while unauthenticated.
  const isAuthenticated = useIsAuthenticated();

  // Remember the last payload that was successfully saved so we can avoid
  // sending identical data to the backend over and over again.
  const lastPayloadRef = useRef<string>();

  /**
   * RESTORE on mount (once)
   */
  useEffect(() => {
    if (!isAuthenticated || !isInitialLoadRef.current) return;
    isInitialLoadRef.current = false;

    setIsRestoring(true);
    loadDraftState({
      variables: { category },
      onCompleted: (data) => {
        if (data?.draftState?.payload) {
          hydrateFn(data.draftState.payload as T);
          setHasRestoredState(true);
        }
        setIsRestoring(false);
      },
      onError: () => setIsRestoring(false),
      fetchPolicy: "network-only",
    });
  }, [isAuthenticated, category, hydrateFn, loadDraftState]);

  /**
   * DEBOUNCED SAVE whenever snapshotFn() output changes.
   */
  const debouncedSave = useMemo(() =>
    debounce(async (payload: T) => {
      if (!isAuthenticated) return; // do not attempt to save without credentials

      // Serialise for a cheap deep-equal check
      const serialised = JSON.stringify(payload);
      if (serialised === lastPayloadRef.current) return; // already saved, skip

      try {
        setIsSaving(true);
        await saveDraftState({ variables: { category, payload } });
        lastPayloadRef.current = serialised; // remember what we just saved
      } finally {
        setIsSaving(false);
      }
    }, 5000) // generous debounce → fires 5 s after the last change
  , [isAuthenticated, category, saveDraftState]);

  // Observe data changes (naïve deep-compare prevented by debounce)
  useEffect(() => {
    if (!isAuthenticated || isRestoring) return; // skip while unauthenticated or restoring
    const payload = snapshotFn();
    debouncedSave(payload);
    return () => debouncedSave.cancel();
  }, [isAuthenticated, snapshotFn, isRestoring, debouncedSave]);

  /** Clear draft both locally & server side */
  const clearDraft = useCallback(async () => {
    debouncedSave.cancel();
    if (isAuthenticated) {
      await clearDraftStateMutation({ variables: { category } });
    }
    setHasRestoredState(false);
  }, [isAuthenticated, category, clearDraftStateMutation, debouncedSave]);

  return { hasRestoredState, isRestoring, isSaving, clearDraft };
} 